import { cloneDeep } from "lodash"

import type { FeedbackPerLabelAndReason } from "../../types/generated/api/FeedbackPerLabelAndReason"
import type { InsightList } from "../../types/generated/api/InsightList"
import type { LabelInsight } from "../../types/generated/api/LabelInsight"
import { FEATURE_NESTING_SEPARATOR } from "../../utils/constants"
import type { LabelData, LabelInsightProps, LabelsCriteriaInsightProps } from "./interfaces"

/**
 * Helper function to replace FEATURE_NESTING_SEPARATOR (>>>) with "." in object keys.
 *
 * @param record - An object with keys that may contain FEATURE_NESTING_SEPARATOR
 * @returns A new object with transformed keys where >>> is replaced with .
 *
 * @example
 * const input = { "feature>>>nested>>>field": 123 }
 * const output = transformFeatureKeys(input)
 * // Returns: { "feature.nested.field": 123 }
 */
export function transformFeatureKeys(record: Record<string, string | number>): Record<string, string | number> {
  return Object.entries(record).reduce(
    (acc, [key, value]) => {
      const transformedKey = key.replaceAll(FEATURE_NESTING_SEPARATOR, ".")
      acc[transformedKey] = value
      return acc
    },
    {} as Record<string, string | number>,
  )
}

export function CalculateStandardDeviation(values: number[]): number {
  if (values.length === 0) return NaN // Guard against empty array

  // Calculate the mean (average) of the array
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length

  // Calculate the sum of squared differences from the mean
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0)

  // If it's a sample, divide by (n - 1), otherwise divide by n
  const divisor = values.length

  // Return the square root of the variance (standard deviation)
  return Math.sqrt(variance / divisor)
}

interface Props {
  max: number
  min: number
  count: number
  Q1: number
  Q3: number
}

export function generateBoxplotData(props: Props): number[] {
  const { min, max, count, Q1, Q3 } = props

  // Helper function to generate a random number within a specified range
  const generateValueInRange = (min: number, max: number): number => {
    return Math.random() * (max - min) + min
  }

  // Helper function to generate data while respecting the fixed min and max
  const generateDataRespectingBounds = (): number[] => {
    const data: number[] = []

    // Add the fixed min and max values to the data
    data.push(min, max)

    // Generate values for the quartiles (Q1,  Q3)
    data.push(Q1, Q3)

    // Generate the lower tail (values between min and Q1)
    const lowerTailCount = Math.floor(count / 2 - 1) // Remaining data points in the lower half
    for (let i = 0; i < lowerTailCount; i++) {
      data.push(generateValueInRange(min, Q1))
    }

    // Generate the upper tail (values between Q3 and max)
    const upperTailCount = Math.floor(count / 2) // Remaining data points in the upper half
    for (let i = 0; i < upperTailCount; i++) {
      data.push(generateValueInRange(Q3, max))
    }

    // Sort the data to simulate ordered data for the box plot
    data.sort((a, b) => a - b)

    // Ensure that the first value is the min and the last value is the max
    if (data[0] !== min) {
      data[0] = min // Ensure min is the first value
    }
    if (data[data.length - 1] !== max) {
      data[data.length - 1] = max // Ensure max is the last value
    }

    return data
  }

  // Generate data while respecting the bounds
  const data = generateDataRespectingBounds()

  return data
}

/**
 * Generates a summary of success and failure rates for label insights or feedback data.
 *
 * @param props - The input properties
 * @param props.data - Label insight or feedback data to be analyzed
 * @param props.isLoading - Loading state indicator
 * @param props.isFeedback - Flag to determine if processing feedback data instead of labels
 * @returns An object containing "Failed" and "Success" statistics with:
 *          - value: The count of items in each category
 *          - percent: The percentage each category represents of the total
 *          Returns an empty object if data is still loading
 *
 * @example
 * const summary = generateLabelInsightsSummaryChartData({
 *   data: labelInsightData,
 *   isLoading: false,
 *   isFeedback: false
 * });
 * // Returns: {
 * //   Failed: { value: 10, percent: 20 },
 * //   Success: { value: 40, percent: 80 }
 * // }
 */
export const generateLabelInsightsSummaryChartData = (props: {
  data?: LabelInsightProps | LabelInsight
  isLoading: boolean
  isFeedback?: boolean
}): Record<
  "Failed" | "Success",
  {
    value: number
    percent: number
  }
> => {
  const { data, isFeedback, isLoading } = props

  const result: Record<
    string,
    {
      value: number
      percent: number
    }
  > = {}

  // Return empty result if data is still loading
  if (isLoading) return result

  // Check for failed predictions (labeled as "NA" in our system)
  const failedPredictions = data?.data?.find((item) =>
    isFeedback ? (item as LabelData).feedback === "NA" : item.label === "NA",
  )

  if (failedPredictions) {
    // Calculate percentages for failed predictions using total count as denominator
    const failedPredictionsPercentage = (failedPredictions.count / (data?.count ?? 1)) * 100

    // Add failed predictions data at index 1
    result["Failed"] = {
      value: failedPredictions.count,
      percent: failedPredictionsPercentage,
    }

    // Add successful predictions data at index 0 (total - failed)
    result["Success"] = {
      value: (data?.count ?? 0) - failedPredictions.count,
      percent: 100 - failedPredictionsPercentage,
    }

    return result
  }

  // If no failed predictions found, consider all predictions successful
  result["Success"] = {
    value: data?.count ?? 0,
    percent: 100,
  }

  return result
}

/**
 * Generates formatted chart data from label insights or feedback data.
 *
 * @param props - The input properties
 * @param props.data - Label insight or feedback data to be transformed
 * @param props.isLoading - Loading state indicator
 * @param props.isFeedback - Flag to determine if processing feedback data instead of labels
 * @returns An array of chart-compatible data objects containing:
 *          - name: The label or feedback value
 *          - value: The count for this label/feedback
 *          - percent: The percentage this label/feedback represents of the total
 *
 * @example
 * const chartData = generateLabelInsightChartData({
 *   data: labelInsightData,
 *   isLoading: false,
 *   isFeedback: false
 * });
 */
export const generateLabelInsightChartData = (props: {
  data?: LabelInsightProps | LabelInsight
  isLoading: boolean
  isFeedback?: boolean
}): {
  name: string
  value: number
  percent: number
}[] => {
  const { data, isFeedback, isLoading } = props

  const result: {
    name: string
    value: number
    percent: number
  }[] = []

  // Return empty result if data is still loading to prevent processing undefined data
  if (isLoading) return result

  // Transform each label item into chart-compatible format with percentage calculations
  data?.data?.forEach((item) => {
    result.push({
      name: isFeedback ? ((item as LabelData).feedback as string) : (item.label as string),
      value: item.count,
      // Calculate percentage with respect to total count
      percent: (item.count / (data?.count ?? 1)) * 100,
    })
  })

  return result
}

export const generateLabelCriteriaInsightData = (props: {
  data?: LabelsCriteriaInsightProps | FeedbackPerLabelAndReason // Input data containing label criteria insights or feedback
  isLoading: boolean // Indicates if the data is still loading
  isEmpty: boolean // Indicates if the data is empty
}): {
  labels: string[] // Array of labels extracted from the data
  labelReasons: Record<string, string | number>[] // Array of objects containing reasons and their corresponding counts
  reasons: Record<string, string[]> // Object mapping labels to their associated reasons
} => {
  const { data, isLoading, isEmpty } = props

  // Create a deep copy of the data to avoid mutating the original
  const labelsCopy = cloneDeep(data?.data)
  const labelReasons: Array<Record<string, string | number>> = [] // Array to hold label reason data
  const reasons: Record<string, Set<string>> = {} // Object to hold unique reasons for each label

  // Return early if data is still loading or empty
  if (isLoading || isEmpty) return { labels: [], labelReasons: [{}], reasons: {} }

  // Calculate percentages for each reason associated with the labels
  for (const label in labelsCopy) {
    const reasonsSet = (reasons[label] = reasons[label] || new Set<string>()) // Initialize a set for unique reasons

    // Iterate over each reason and its associated feedbacks
    Object.entries(labelsCopy[label] ?? {}).forEach(([reason, feedbacks]) => {
      const labelItem: Record<string, string | number> = {
        // Reversing label and reason to fix tooltip on graph
        label: reason, // The reason for the label
        reason: label, // The label itself
      }

      // Add feedback counts to the label item, excluding "NA" feedback
      Object.entries(feedbacks).forEach(([feedbackKey, feedbackCount]) => {
        if (feedbackKey !== "NA") labelItem[feedbackKey] = feedbackCount
      })

      reasonsSet.add(reason) // Add the reason to the set for uniqueness
      labelReasons.push(labelItem) // Push the label item to the results array
    })
  }

  // Sort label reasons based on the total counts in descending order
  labelReasons.sort(
    (a, b) =>
      Object.values(b).reduce((accumulator: number, currentValue) => accumulator + +currentValue || 0, 0) -
      Object.values(a).reduce((accumulator: number, currentValue) => accumulator + +currentValue || 0, 0),
  )

  // Filter out label reasons that have a total count of zero
  const filteredLabelReasons = labelReasons.filter(
    (labelReason) =>
      Object.values(labelReason).reduce((accumulator: number, currentValue) => accumulator + +currentValue || 0, 0) !==
      0,
  )

  // Convert sets of reasons to arrays for easier handling
  const reasonsByLabel: Record<string, string[]> = {}
  for (const label in reasons) {
    reasonsByLabel[label] = Array.from(reasons[label] ?? []) // Convert each set to an array
  }

  // Return the structured data containing labels, filtered label reasons, and reasons by label
  return {
    labels: Object.keys(labelsCopy ?? {}), // Extract labels from the copied data
    labelReasons: filteredLabelReasons, // Return the filtered label reasons
    reasons: reasonsByLabel, // Return the reasons organized by label
  }
}

export const formatInsightsData = (
  insightsNames: string[],
  groupedInsights: Partial<Record<InsightList.type, InsightList[]>>,
): InsightList[] =>
  insightsNames
    .map((insightType) => {
      const type = insightType as InsightList.type
      return groupedInsights[type]?.[0]
    })
    .filter((item) => !!item)
